"use client"

import LinkButton from "components/Buttons/Link"
import LargeBlocksSVG from "images/Icons/BlocksL.inline.svg"
import SmallBlocksSVG from "images/Icons/BlocksM.inline.svg"
import LargeBullseyeSVG from "images/Icons/BullseyeL.inline.svg"
import SmallBullseyeSVG from "images/Icons/BullseyeM.inline.svg"
import LargeGearSVG from "images/Icons/GearL.inline.svg"
import SmallGearSVG from "images/Icons/GearM.inline.svg"
import LargeGraphSVG from "images/Icons/GraphL.inline.svg"
import SmallGraphSVG from "images/Icons/GraphM.inline.svg"
import LargeHeartSVG from "images/Icons/HeartL.inline.svg"
import SmallHeartSVG from "images/Icons/HeartM.inline.svg"
import LargeSetSVG from "images/Icons/SetL.inline.svg"
import SmallSetSVG from "images/Icons/SetM.inline.svg"
import LargeUserSVG from "images/Icons/UserL.inline.svg"
import SmallUserSVG from "images/Icons/UserM.inline.svg"
import LargeUsersSVG from "images/Icons/UsersL.inline.svg"
import SmallUsersSVG from "images/Icons/UsersM.inline.svg"
import LinesSVG from "images/about/values/Lines.inline.svg"
import { css, f, styled } from "library/styled"
import { useMedia } from "library/useMedia"
import colors from "styles/colors"
import textStyles from "styles/text"
import links from "utils/links"

export default function Values() {
	const linkSize = useMedia("large", "large", "large", "medium")
	const gridItems = [
		{
			id: 0,
			largeSvg: LargeGearSVG,
			smallSvg: SmallGearSVG,
			title: "Innovate",
			tagline: "We challenge the status quo and rethink what's possible.",
		},
		{
			id: 1,
			largeSvg: LargeBullseyeSVG,
			smallSvg: SmallBullseyeSVG,
			title: "Ambition",
			tagline: "We aim high and push boundaries to build what others won't.",
		},
		{
			id: 2,
			largeSvg: LargeBlocksSVG,
			smallSvg: SmallBlocksSVG,
			title: "Transparency",
			tagline: "We communicate openly, honestly, and often- building trust.",
		},
		{
			id: 3,
			largeSvg: LargeHeartSVG,
			smallSvg: SmallHeartSVG,
			title: "High Trust",
			tagline:
				"We assume positive intent and give each other the benefit of the doubt.",
		},
		{
			id: 4,
			largeSvg: LargeUsersSVG,
			smallSvg: SmallUsersSVG,
			title: "Team First",
			tagline: "We win as one team, with mutual respect and shared goals.",
		},
		{
			id: 5,
			largeSvg: LargeGraphSVG,
			smallSvg: SmallGraphSVG,
			title: "Velocity",
			tagline:
				"We move fast, learn faster, and don't let perfect get in the way of progress.",
		},
		{
			id: 6,
			largeSvg: LargeUserSVG,
			smallSvg: SmallUserSVG,
			title: "Customer-Centric",
			tagline:
				"We put customers at the center of every decision. Their success is our north star.",
		},
		{
			id: 7,
			largeSvg: LargeSetSVG,
			smallSvg: SmallSetSVG,
			title: "Ownership",
			tagline:
				"Everyone here is empowered to make decisions and drive results.",
		},
	]

	return (
		<Wrapper>
			<Top>
				<Heading>What We Stand For, How We Work</Heading>
				<Subheading>
					<P>
						Our values are more than just words—they're how we operate, make
						decisions, and treat one another.
					</P>
					<LinkButton href={links.careers} variant="lightAlt" size={linkSize}>
						See Open Positions
					</LinkButton>
				</Subheading>
			</Top>
			<GridWrapper>
				<Lines />
				<Grid>
					{gridItems.map((item) => {
						const LargeIcon = item.largeSvg
						const SmallIcon = item.smallSvg

						return (
							<GridItem key={item.id}>
								<DesktopIcon>
									<LargeIcon />
								</DesktopIcon>
								<MobileIcon>
									<SmallIcon />
								</MobileIcon>
								<GridItemTextContent>
									<GridItemTitle>{item.title}</GridItemTitle>
									<GridItemTagline>{item.tagline}</GridItemTagline>
								</GridItemTextContent>
							</GridItem>
						)
					})}
				</Grid>
			</GridWrapper>
		</Wrapper>
	)
}

const Wrapper = styled("section", {
	...f.responsive(css`
		display: grid;
		place-items: center;
		width: 100%;
		padding: 180px 0;
	`),
	...f.tablet(css`
		padding: 76px 0 165px;
	`),
	...f.mobile(css`
		padding: 24px 0 96px;
	`),
})

const Top = styled("div", {
	...f.responsive(css`
		display: flex;
		gap: 163px;
		width: 1032px;
		margin: 0 auto 96px;
	`),
	...f.tablet(css`
		gap: 38px;
		width: 896px;
	`),
	...f.mobile(css`
		gap: 32px;
		width: 287px;
		flex-direction: column;
		margin: 0 auto 80px;
	`),
})

const Heading = styled("h4", {
	...f.responsive(css`
		${textStyles.h4};
		color: ${colors.green500};
		width: 526px;
	`),
	...f.tablet(css`
		width: 516px;
	`),
	...f.mobile(css`
		${textStyles.h6};
		width: 100%;
	`),
})

const Subheading = styled("div", {
	...f.responsive(css`
		display: flex;
		flex-direction: column;
		gap: 39px;
		width: 343px;
	`),
	...f.tablet(css`
		gap: 26px;
		width: 337px;
	`),
	...f.mobile(css`
		gap: 22px;
		width: 100%;
	`),
})

const P = styled("p", {
	...f.responsive(css`
		${textStyles.bodyR};
		color: ${colors.green400};
		margin-left: 5px;
	`),
	...f.mobile(css`
		${textStyles.bodyS};
	`),
})

const GridWrapper = styled("div", {
	...f.unresponsive(css`
		border: 1px solid ${colors.green200};
	`),
	...f.responsive(css`
		position: relative;
		border-radius: 42px;
		width: 1082px;
		padding: 24px;
		margin: 0 auto;
	`),
	...f.tablet(css`
		width: 898px;
	`),
	...f.mobile(css`
		width: 329px;
		padding: 20px;
		border-radius: 24px;
	`),
})

const Lines = styled(LinesSVG, {
	...f.responsive(css`
		position: absolute;
		top: -91px;
		left: 53px;
		width: 14px;
		height: 96px;
	`),
	...f.mobile(css`
		height: 60px;
		top: -61px;
		left: 20px;
	`),
})

const Grid = styled("div", {
	...f.responsive(css`
		display: grid;
		grid-template-columns: repeat(6, 172px);
		grid-template-rows: repeat(3, 314px);
		border-radius: 28px;
	`),
	...f.tablet(css`
		grid-template-columns: repeat(2, 424px);
		grid-template-rows: repeat(4, 314px);
	`),
	...f.mobile(css`
		display: flex;
		flex-direction: column;
	`),
})

const GridItem = styled("div", {
	"--border-size": "1px",
	...f.unresponsive(css`
		border: var(--border-size) solid ${colors.green100};
	`),
	...f.responsive(css`
		display: flex;
		flex-direction: column;
		padding: 28px;
		justify-content: space-between;
		grid-column: span 2;

		&:nth-of-type(1) {
			border-radius: 28px 0 0;
			border-right: none;
			border-bottom: none;
		}

		&:nth-of-type(2) {
			border-right: none;
			border-bottom: none;
		}

		&:nth-of-type(3) {
			border-radius: 0 28px 0 0;
			border-bottom: none;
		}

		&:nth-of-type(4) {
			border-right: none;
			border-bottom: none;
		}

		&:nth-of-type(5) {
			border-right: none;
			border-bottom: none;
		}

		&:nth-of-type(6) {
			border-bottom: none;
		}

		&:nth-of-type(7) {
			grid-column: 1 / 4;
			border-radius: 0 0 0 28px;
			border-right: none;
		}

		&:nth-of-type(8) {
			grid-column: 4 / -1;
			border-radius: 0 0 28px;
		}
	`),
	...f.tablet(css`
		grid-column: span 1;

		&:nth-of-type(2) {
			border-radius: 0 28px 0 0;
			border-right: var(--border-size) solid ${colors.green100};
		}

		&:nth-of-type(3) {
			border-radius: unset;
			border-right: none;
		}

		&:nth-of-type(4) {
			border-right: var(--border-size) solid ${colors.green100};
		}

		&:nth-of-type(7) {
			grid-column: span 1;
		}

		&:nth-of-type(8) {
			grid-column: span 1;
		}
	`),
	...f.mobile(css`
		width: 287px;
		height: 230px;

		&:nth-of-type(odd) {
			border-right: var(--border-size) solid ${colors.green100};
			border-bottom: none;
		}

		&:nth-of-type(even) {
			border-right: var(--border-size) solid ${colors.green100};
		}

		&:nth-of-type(1) {
			border-radius: 16px 16px 0 0;
		}

		&:nth-of-type(2) {
			border-bottom: none;
		}

		&:nth-of-type(3) {
			border-radius: unset;
		}

		&:nth-of-type(4) {
			border-bottom: none;
		}

		&:nth-of-type(6) {
			border-bottom: none;
		}

		&:nth-of-type(7) {
			border-radius: unset;
		}

		&:nth-of-type(8) {
			border-radius: 0 0 16px 16px;
			border-bottom: var(--border-size) solid ${colors.green100};
		}
	`),
})

const DesktopIcon = styled("div", {
	...f.responsive(css`
		width: 50px;
		height: 50px;

		svg {
			path {
				stroke: ${colors.green200};
			}
		}
	`),
	...f.mobile(css`
		display: none;
	`),
})

const MobileIcon = styled("div", {
	...f.responsive(css`
		display: none;
	`),
	...f.mobile(css`
		display: flex;
		width: 32px;
		height: 32px;

		svg {
			path {
				stroke: ${colors.green200};
			}
		}
	`),
})

const GridItemTextContent = styled("div", {
	...f.responsive(css`
		display: flex;
		flex-direction: column;
		gap: 24px;
		width: 288px;
	`),
	...f.tablet(css`
		width: 368px;
	`),
	...f.mobile(css`
		width: 231px;
	`),
})

const GridItemTitle = styled("h6", {
	...f.responsive(css`
		${textStyles.bodyXL};
		color: ${colors.green500};
	`),
	...f.tablet(css`
		${textStyles.h6};
	`),
})

const GridItemTagline = styled("p", {
	...f.responsive(css`
		${textStyles.bodyS};
		color: ${colors.green400};
	`),
	...f.tablet(css`
		${textStyles.bodyR};
	`),
})
